import {
  GreyGrey20,
  GreyGrey30,
  GreyGrey70,
  Primary14SemiBold,
  Box
} from '@sequencehq/core-components'
import { QuoteTemplateItem, TemplateItemSkeleton } from './QuoteTemplateItem'
import { useSequenceInfiniteQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import templateListBackground from './templateListBackground.png'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { memo, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'

const ITEM_WIDTH = 144
const ITEM_GAP = 24
const ITEM_STRIDE = ITEM_WIDTH + ITEM_GAP
const STRIP_HEIGHT = 224

// The strip's fixed rendered height, passed to MagicTable's `scrollAreaHeader`
// to reserve its scroll space. Keep in sync with the layout below:
//   title row  48 = 16px top pad + 16px line-height + 16px bottom pad
//   cards     240 = STRIP_HEIGHT + 16px bottom pad
//   border      1 = bottom border
export const QUOTE_TEMPLATES_LIST_HEIGHT = 48 + (STRIP_HEIGHT + 16) + 1

export const QuoteTemplatesList = memo(() => {
  const templatesInfiniteQuery = useSequenceInfiniteQuery(
    dashboard20240730Client.getListQuoteTemplates,
    {
      limit: 100
    }
  )

  const [infiniteRef] = useInfiniteScroll({
    loading: templatesInfiniteQuery.infiniteQuery.isPending,
    hasNextPage: templatesInfiniteQuery.infiniteQuery.hasNextPage,
    onLoadMore: templatesInfiniteQuery.infiniteQuery.fetchNextPage,
    /**
     * Load more when we approach 5 templates from the right
     */
    rootMargin: `0px 0px ${ITEM_WIDTH * 5}px 0px`
  })

  const allTemplates =
    (templatesInfiniteQuery.infiniteQuery.data?.pages ?? []).flatMap(
      page => page.items
    ) ?? []

  const hasNextPage = templatesInfiniteQuery.infiniteQuery.hasNextPage
  const virtualItemCount = allTemplates.length + (hasNextPage ? 1 : 0)

  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: virtualItemCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ITEM_STRIDE,
    horizontal: true,
    overscan: 3
  })

  if (
    templatesInfiniteQuery.infiniteQuery.isPending ||
    allTemplates.length === 0
  ) {
    return null
  }

  return (
    <Box
      data-testid="quoteTemplates.list"
      width="100%"
      position="relative"
      borderBottom={`1px solid ${GreyGrey30}`}
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `url(${templateListBackground}) ${GreyGrey20} repeat`,
        backgroundSize: '500px 500px',
        opacity: 0.15,
        zIndex: 0
      }}
    >
      <Box
        color={GreyGrey70}
        {...Primary14SemiBold}
        padding="16px"
        position="relative"
      >
        Create new quote from template
      </Box>
      <Box
        ref={scrollRef}
        overflowX="auto"
        overflowY="hidden"
        padding="16px"
        paddingTop="0"
        position="relative"
        width="100%"
      >
        <Box
          position="relative"
          width={`${virtualizer.getTotalSize()}px`}
          height={`${STRIP_HEIGHT}px`}
        >
          {virtualizer.getVirtualItems().map(virtualItem => {
            const isLoaderSlot = virtualItem.index === allTemplates.length
            const template = allTemplates[virtualItem.index]
            return (
              <Box
                key={virtualItem.key}
                position="absolute"
                top="0"
                left="0"
                width={`${ITEM_WIDTH}px`}
                transform={`translateX(${virtualItem.start}px)`}
              >
                {isLoaderSlot ? (
                  <Box ref={infiniteRef}>
                    <TemplateItemSkeleton />
                  </Box>
                ) : (
                  <QuoteTemplateItem
                    quoteTemplateId={template.id}
                    delay={150}
                  />
                )}
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
})
